import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { Add } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import financeService from '../../services/financeService';
import { FinanceSettings } from '../../types/finance';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import { useChart } from '../../components/finance/ledger/useChart';
import JournalEntriesTable from '../../components/finance/journal/JournalEntriesTable';
import JournalEntryDrawer from '../../components/finance/journal/JournalEntryDrawer';
import ManualJournalDialog from '../../components/finance/journal/ManualJournalDialog';

/**
 * Finance → Journal. Every posted voucher. Manual journals waiting for a
 * second person are in Finance → Approvals.
 */
const JournalPage: React.FC = () => {
  const { canWrite } = useModulePermissions();
  const { enqueueSnackbar } = useSnackbar();
  const { options } = useChart();
  const [settings, setSettings] = useState<FinanceSettings | null>(null);
  const [openEntryId, setOpenEntryId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = useCallback(() => setRefreshKey(k => k + 1), []);


  useEffect(() => {
    financeService.getSettings().then(setSettings)
      .catch((e: any) => enqueueSnackbar(e?.message || 'Could not load accounting preferences.', { variant: 'error' }));
  }, [enqueueSnackbar]);


  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'flex-start' }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Journal</Typography>
          <Typography variant="body2" color="text.secondary">
            Every posting as a numbered, balanced voucher. Nothing is edited; mistakes are reversed.
          </Typography>
        </Box>
        {canWrite('finance_journal') && settings && (
          <Button variant="contained" disableElevation startIcon={<Add />} onClick={() => setCreating(true)} sx={{ borderRadius: 2, whiteSpace: 'nowrap' }}>
            New journal
          </Button>
        )}
      </Stack>

      <JournalEntriesTable accountOptions={options.ledger} refreshKey={refreshKey} onOpen={setOpenEntryId} />

      <JournalEntryDrawer entryId={openEntryId} onClose={() => setOpenEntryId(null)} onChanged={refresh} />
      {settings && (
        <ManualJournalDialog
          open={creating}
          onClose={() => setCreating(false)}
          onSaved={refresh}
          accountOptions={options.postable}
          requiresApproval={settings.requireJournalApproval}
        />
      )}
    </Box>
  );
};

export default JournalPage;
